import FileSaver from 'file-saver';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import createAxiosInstance from "../../../utils/axiosConfig";
import { export_data, question_flows, score_history_domain } from "../../../utils/endpoints";
import {
	FrameworkDomain
} from "../../../utils/interfaces";
import Card from "./cards";
import SideScreenLLM from "./sideScreen";
import ScoreHistory from "./sideScreen/scoreHistory";
import AdminTables from '../adminTables';
import ScoreHistoryIcon from '../../../assets/svg/scoreHistoryIcon';
import EnhanceAI from '../../../assets/svg/enhanceAI';
import { processStreamingHeaders } from "../../../utils/axiosConfig";
import DomainEditSidebar from '../domainEditSidebar';
import AddDomainSidebar from '../addDomainSidebar';
import AddIcon from '../../../assets/svg/addIcon';

const Domains = ({
	domains,
	handleQuit,
	frameworkId,
	selectDomain,
	refresh,
	frameworkData,
	onRefreshRequest
}: {
	domains: FrameworkDomain[];
	handleQuit: () => void;
	frameworkId?: number;
	selectDomain?: (domain: FrameworkDomain) => void;
	refresh?: number;
	frameworkData?: any;
	onRefreshRequest?: () => void;
}) => {
	const [selectedDomain, setSelectedDomain] = useState<FrameworkDomain | null>(null);
	const { logout, userId, userData } = useAuth();
	const navigate = useNavigate();
	const [domainScores, setDomainScores] = useState<any[]>([]);

	const [isSideScreenVisible, setIsSideScreenVisible] =
		useState<boolean>(false);
	const [refreshState, setRefreshState] = useState<number>(0);

	const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
	
	// Edit domain states
	const [showDomainEditSidebar, setShowDomainEditSidebar] = useState<boolean>(false);
	const [editDomainData, setEditDomainData] = useState<FrameworkDomain | null>(null);
	
	// Add domain states
	const [showAddDomainSidebar, setShowAddDomainSidebar] = useState<boolean>(false);

	const calculateAverageScore = (scores: any[]) => {
		const totalScore = scores.reduce(
			(sum, score) => sum + score?.total_score,
			0
		);
		return totalScore / scores.length;
	};

	const calculateDomainAverageScore = () => {
		const validScores = domainScores.filter((score) =>
			domains.some((domain) => domain.id === score.domain_id)
		);
		return calculateAverageScore(validScores);
	};

	useEffect(() => {
		setRefreshState(refreshState + 1);
	}, [selectedDomain]);

	// Auto-select domain when domains are loaded - restore from localStorage or default to first
	useEffect(() => {
		if (domains && domains.length > 0 && !selectedDomain && frameworkId) {
			let domainToSelect = domains[0];
			
			// Try to restore from localStorage for this framework
			const savedDomainId = localStorage.getItem(`maturity_selectedDomainId_${frameworkId}`);
			if (savedDomainId) {
				const savedDomain = domains.find(d => d.id.toString() === savedDomainId);
				if (savedDomain && savedDomain.is_enabled) {
					domainToSelect = savedDomain;
				}
			}
			
			setSelectedDomain(domainToSelect);
			if (selectDomain) {
				selectDomain(domainToSelect);
			}
		}
	}, [domains, selectDomain, selectedDomain, frameworkId]);

	useEffect(() => {
		const getDomainScore = async () => {
			if (!userId) return;
			
			setIsRefreshing(true);
			console.log("Refreshing domain scores...");
			
			try {
				const response = await createAxiosInstance().get(
					`scores/user/${userId}/domain`
				);

				if (response.status == 401 || response.status == 403) {
					console.log("user token expired");
					logout();
					navigate("/");
					return;
				}

				setDomainScores(response.data);
				console.log("Domain scores refreshed:", response.data);
			} catch (e: any) {
				console.log(e, "Error");
			} finally {
				setIsRefreshing(false);
			}
		};

		// Add delay when refreshing (not on initial load)
		if (refresh && refresh > 0) {
			setTimeout(() => {
				getDomainScore();
			}, 1000); // Start domain refresh after category and flow refresh
		} else {
			getDomainScore(); // Immediate on initial load
		}
	}, [refresh, userId]);

	const handleAIButton = (domain: FrameworkDomain) => {
		setIsSideScreenVisible(!isSideScreenVisible);
		setIsOpen(true);
		setLoading(true);
		setLLMContent(""); // Clear content immediately to show spinner
		setFirstChunkReceived(false); // Reset flag for new request
		fetchLLMResponse(domain.id);
		setCurrentLLMDomain(domain);
	};
	const handleExportData = async() => {
		if (!userId) window.location.href = "/";
		try {
			const scoreHistory = await createAxiosInstance().get(
				export_data(userId||0), {
					responseType: "blob", 
				  }
			);

			if (scoreHistory.status == 401 || scoreHistory.status == 403) {
				console.log("user token expired");
				return;
			}
			
			const contentDisposition = scoreHistory.headers["content-disposition"];
			let filename = "downloaded_file.xlsx"; // Default filename

			if (contentDisposition) {
				const match = contentDisposition.match(/filename="?(.+?)"?$/);
				filename = match?.[1] || filename;
			}
			const blob = new Blob([scoreHistory.data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			  });

			FileSaver.saveAs(blob, filename.replace(/["']/g, ''));
		} catch (e) {
			console.log(e);
		} finally {
			console.log("Finally Executed");
		}
		
	};
	const [isOpen, setIsOpen] = useState(false);
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);
	const [historyLoading, setHistoryLoading] = useState<boolean>(false);
	const [historyContent, setHistoryContent] = useState<any[]>([]);

	const [eventSource, setEventSource] =
		useState<ReadableStreamDefaultReader | null>(null);
	const [firstChunkReceived, setFirstChunkReceived] = useState<boolean>(false);

	const closeChatHistory = () => {
		// Cancel any active EventSource
		if (eventSource) {
			eventSource.cancel();
			setEventSource(null);
		}
		// Reset all states
		setIsOpen(false);
		setCurrentLLMDomain(null);
		setLLMContent("");
		setLoading(false);
		setFirstChunkReceived(false);
	};

	const closeScoreHistory = () => {
		setIsHistoryOpen(false);
		setHistoryContent([]);
	};

	const openScoreHistory = (domainId: number) => {
		setIsHistoryOpen(true);
		fetchScoreHistory(domainId);
	};

	const fetchScoreHistory = async (domainId: number) => {
		if (!userId) window.location.href = "/";
		setHistoryLoading(true);
		try {
			const scoreHistory = await createAxiosInstance().get(
				score_history_domain(userId || 0, domainId)
			);

			if (scoreHistory.status == 401 || scoreHistory.status == 403) {
				// 401 is for expired token
				// 403 is for forbidden access (blocked by GPT.)
				console.log("user token expired");
				logout();
				navigate("/");
				return;
			}

		setHistoryContent(scoreHistory?.data || []);
		} catch (e) {
			console.log(e);
		} finally {
			setHistoryLoading(false);
		}
	};

	const [LLMContent, setLLMContent] = useState<any>("");
	const [currentLLMDomain, setCurrentLLMDomain] =
		useState<FrameworkDomain | null>(null);
	const [currentLLMIndex, setCurrentLLMIndex] = useState<number>(0);
	const [payloads, setPayloads] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(false);

	const handleNext = () => {
		if (currentLLMIndex <= payloads.length - 1) {
			setCurrentLLMIndex(currentLLMIndex + 1);
			setLoading(true);
			setLLMContent(""); // Clear content to show spinner
			setFirstChunkReceived(false); // Reset flag for new request
			if (currentLLMDomain) fetchLLMResponse(currentLLMDomain?.id);
		}
	};

	const handlePrev = () => {
		if (currentLLMIndex !== 0) {
			setCurrentLLMIndex(currentLLMIndex > 0 ? currentLLMIndex - 1 : 0);
			setLoading(true);
			setLLMContent(""); // Clear content to show spinner
			setFirstChunkReceived(false); // Reset flag for new request

			if (currentLLMDomain) fetchLLMResponse(currentLLMDomain?.id);
		}
	};

	const fetchDomainBasedUserAnswers = async (
		user_id: number,
		domain_id: number
	) => {
		try {
			const response = await createAxiosInstance().get(
				`/user_answers/${user_id}/domain/${domain_id}`
			);

			console.log(response);
			return response.data;
		} catch (e: any) {
			console.log(e, "error");
		}
	};

	const fetchQuestionsFlow = async (domainId: number) => {
		if (!domainId) return;

		try {
			const questionsFlowResponse = await createAxiosInstance().get(
				question_flows(domainId)
			);

			if (
				questionsFlowResponse.status == 401 ||
				questionsFlowResponse.status == 403
			) {
				// 401 is for expired token
				// 403 is for forbidden access (blocked by GPT.)
				console.log("user token expired");
				logout();
				navigate("/");
				return;
			}
			return questionsFlowResponse.data;
		} catch (e: any) {
			console.log(e);
		}
	};

	const fetchLLMResponse = async (domain_id: number) => {
		setLoading(true);
		try {
			// fetch all useranswers for a given domain sorted by Date...
			// pick ones with latest date...
			// prepare the DS...
			// send request to /assessment endpoint.
			// show response in markdown Structure (set LLM content state.).
			if (userId) {
				// Close previous event source if it exists
				if (eventSource) {
					eventSource.cancel();
				}
				const token = localStorage.getItem("token");

				const userAnswers = await fetchDomainBasedUserAnswers(
					userId,
					domain_id
				);
				if (userAnswers.status == 401 || userAnswers.status == 403) {
					// 401 is for expired token
					// 403 is for forbidden access (blocked by GPT.)
					console.log("user token expired");

					logout();
					navigate("/");
					return;
				}

				setPayloads(userAnswers);
				const questionsFlow = await fetchQuestionsFlow(domain_id);

				const payload = {
					...userAnswers,
				};

				// Get the current payload based on currentLLMIndex
				const currentPayload = payload[currentLLMIndex];
				
				// Find the matching question flow by framework_area instead of using array index
				const matchingFlow = questionsFlow?.find(
					(flow: any) => flow.description === currentPayload?.framework_area
				);
				
				if (!matchingFlow) {
					console.error("No matching question flow found for framework_area:", currentPayload?.framework_area);
					setLLMContent("No matching assessment flow found. Please ensure the assessment is completed correctly.");
					setLoading(false);
					return;
				}

				// Send the payload to the /assessment endpoint
				const assessmentResponse = await fetch(
					process.env.REACT_APP_SERVER_URL + "/assessment",
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${token}`,
						},
						body: JSON.stringify({
							...currentPayload,
							question_flow_id: matchingFlow.id,
						}),
					}
				);

				// Process headers for token refresh
				processStreamingHeaders(assessmentResponse);

				// Set the LLM content state with the response
				// setLLMContent(assessmentResponse.data);
				if (assessmentResponse?.body) {
					const reader = assessmentResponse.body.getReader();
					setEventSource(reader);
				}
			}
		} catch (e: any) {
			console.log(e, "error ");
			// More specific error handling logic here
			if (e instanceof TypeError) {
				// Handle network errors (e.g., network down, CORS issues)
				console.error("Network error:", e);
				return;
			} else {
				// Handle other types of errors
				console.error("Unexpected error:", e);
				return;
			}
		}
	};

	const addToLLMContent = (newContent: string) => {
		setLLMContent((prevContent: any) => `${prevContent}${newContent}`);
	};

	useEffect(() => {
		if (eventSource) {
			const readStream = async () => {
				try {
					while (true) {
						const { done, value } = await eventSource.read();
						if (done) {
							break;
						}

						const buffer = new TextDecoder().decode(value, { stream: true });
						// Split buffer by newline characters
						const lines = buffer.split("\n");
						// Process complete lines
						lines.forEach((line) => {
							if (line.startsWith("data: ")) {
								const dataString = line.substring(6); // Remove 'data: ' prefix
								try {
									const data = JSON.parse(dataString);
									if (data.message) {
										addToLLMContent(data.message);
										if (!firstChunkReceived) {
											setFirstChunkReceived(true);
											setLoading(false);
										}
									}
								} catch (e) {
									console.error("Error parsing JSON:", e);
									return;
								}
							}
						});
					}
				} catch (e) {
					console.error("Error reading stream:", e);
					setEventSource(null);
					setLoading(false);
				} finally {
					setEventSource(null);
					setLoading(false);
				}
			};
			readStream();
		}
	}, [eventSource]);

	const headerTextStyle = {
		color: "#183FCE",
		fontSize: '12px',
		fontWeight: 'bold' as const
	};

	const handleDomainSelect = (domain: FrameworkDomain) => {
		setSelectedDomain(domain);
		// Persist selection with framework context
		localStorage.setItem(`maturity_selectedDomainId_${frameworkId}`, domain.id.toString());
		selectDomain?.(domain);
	};

	const findScoreForDomain = (domainId: number) => {
		return domainScores?.find(
			(score: any) => score.domain_id === domainId
		);
	};

	const handleHistoryOpen = (domainId: number) => {
		openScoreHistory(domainId);
	};

	// Handle edit domain
	const handleEditDomain = (domain: FrameworkDomain) => {
		setEditDomainData(domain);
		setShowDomainEditSidebar(true);
	};

	// Handle close edit sidebar
	const handleCloseDomainEdit = () => {
		setShowDomainEditSidebar(false);
		setEditDomainData(null);
	};

	// Handle domain edit success - refresh domains
	const handleDomainEditSuccess = () => {
		// Close the sidebar
		handleCloseDomainEdit();
		// Notify parent to refresh data elegantly without page reload
		if (onRefreshRequest) {
			onRefreshRequest();
		}
	};

	// Handle add domain
	const handleAddDomain = () => {
		setShowAddDomainSidebar(true);
	};

	// Handle close add domain sidebar
	const handleCloseAddDomain = () => {
		setShowAddDomainSidebar(false);
	};

	// Handle domain add success - refresh domains
	const handleDomainAddSuccess = () => {
		// Close the sidebar
		handleCloseAddDomain();
		// Notify parent to refresh data elegantly without page reload
		if (onRefreshRequest) {
			onRefreshRequest();
		}
	};

	const tableColumns = [
		{
			accessorKey: "title",
			header: () => <span style={headerTextStyle}>Name</span>,
			width: "30%",
			cell: ({ row }: { row: any }) => {
				const domain = row.original;
				const isSelected = selectedDomain?.id === domain.id;
				const isDisabled = !domain.is_enabled;
				return (
					<span style={{
						color: isSelected ? "#FFFFFF" : (isDisabled ? "#B0B0B0" : "#2A2A2A"), 
						fontSize: '16px'
					}}>
						{domain.title}
					</span>
				);
			},
		},
		{
			id: "maturity_score",
			header: () => <span style={headerTextStyle}>Current Maturity Score</span>,
			width: "35%",
			cell: ({ row }: { row: any }) => {
				const domain = row.original;
				const scores = findScoreForDomain(domain.id);
				const displayScore = scores?.total_score ?? 0;
				const hasScore = scores && scores.total_score > 0;
				const isSelected = selectedDomain?.id === domain.id;
				const isDisabled = !domain.is_enabled;
				
				return (
					<span style={{
						color: isSelected ? "#FFFFFF" : (isDisabled ? "#B0B0B0" : (hasScore ? "#5376F0" : "#808080")), 
						fontSize: '16px', 
						textAlign: 'center',
						fontWeight: 'bold'
					}}>
						{displayScore > 0 ? `${displayScore.toFixed(2)}` : "0.00"}
					</span>
				);
			},
		},
		{
			id: "enhance_ai",
			header: () => <span style={headerTextStyle}>Enhance with AI</span>,
			width: "10%",
			cell: ({ row }: { row: any }) => {
				const domain = row.original;
				const isSelected = selectedDomain?.id === domain.id;
				const domainScore = findScoreForDomain(domain.id);
				const hasScore = domainScore && domainScore.total_score > 0;
				const isDisabled = !domain.is_enabled;
				
				return (
					<div
						onClick={(e) => {
							e.stopPropagation();
							if (!isDisabled) {
								handleAIButton(domain);
							}
						}}
						style={{
							fontSize: '2px',
							padding: '4px 8px',
							borderColor: isSelected ? "#FFFFFF" : (isDisabled ? "#CCCCCC" : (hasScore ? "#0027B0" : "#CCCCCC")),
							color: isSelected ? "#FFFFFF" : (isDisabled ? "#CCCCCC" : (hasScore ? "#0027B0" : "#CCCCCC")),
							backgroundColor: "transparent",
							filter: isSelected ? 'brightness(0) invert(1)' : 'none',
							opacity: (isDisabled || !hasScore) ? 0.3 : 1,
							cursor: (isDisabled || !hasScore) ? 'not-allowed' : 'pointer'
						}}
					>
						<EnhanceAI />
					</div>
				);
			},
		},
		{
			id: "history",
			header: () => <span style={headerTextStyle}>History</span>,
			width: "10%",
			cell: ({ row }: { row: any }) => {
				const domain = row.original;
				const scores = findScoreForDomain(domain.id);
				const hasScore = scores && scores.total_score > 0;
				const isSelected = selectedDomain?.id === domain.id;
				const isDisabled = !domain.is_enabled;

				return (
					<div 
						onClick={(e) => {
							e.stopPropagation();
							if (hasScore && !isDisabled) {
								handleHistoryOpen(domain.id);
							}
						}}
						style={{ 
							cursor: (hasScore && !isDisabled) ? 'pointer' : 'default',
							opacity: (hasScore && !isDisabled) ? 1 : 0.3,
							filter: isSelected ? 'brightness(0) invert(1)' : 'none',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center'
						}}
					>
						<ScoreHistoryIcon />
					</div>
				);
			},
		},
		// Add edit column only for admin users and FMA managers (last column)
		...((userData?.is_admin === 1 || userData?.account_type === 5) ? [{
			id: "edit",
			header: () => <span style={headerTextStyle}>Edit</span>,
			width: "5%",
			cell: ({ row }: { row: any }) => {
				const domain = row.original;
				const isSelected = selectedDomain?.id === domain.id;
				const isDisabled = !domain.is_enabled;
				return (
					<div 
						onClick={(e) => {
							e.stopPropagation();
							handleEditDomain(domain);
						}}
						style={{ 
							padding: '4px',
							borderRadius: '4px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							cursor: 'pointer',
							opacity: (!isDisabled) ? 1 : 0.3,
							filter: isSelected ? 'brightness(0) invert(1)' : 'none'
						}}
					>
						<i className="bi bi-pencil" style={{ fontSize: '16px' }}></i>
					</div>
				);
			},
		}] : []),
	];

	// Filter out domains where is_active = false
	const activeDomains = domains.filter(domain => domain.is_active !== false);

	return (
		<div className="container-fluid">
			<div className="row justify-content-center">
				<div className="col-md-12" style={{paddingRight: '0px'}}>
					{/* Header with Framework title and Add button */}
					{(userData?.is_admin === 1 || userData?.account_type === 5) && (
						<div className="d-flex justify-content-between align-items-center mb-3">
							<h6 className="mb-0" style={{fontSize: '1.125rem', fontWeight: 'bold', color: "#000000"}}>
								{frameworkData?.title || 'Framework Domains'}
							</h6>
							<button
								type="button"
								className="btn btn-sm d-flex align-items-center gap-2 mt-3 mx-3 text-white"
								onClick={handleAddDomain}
								style={{
									fontSize: '12px',
									background: '#5376F0',
									borderRadius: '77.125rem',
									transition: 'all 0.3s ease',
								}}
								onMouseEnter={(e) => {
									const target = e.target as HTMLButtonElement;
									target.style.backgroundColor = '#26397E';
									target.style.transform = 'translateY(-1px)';
								}}
								onMouseLeave={(e) => {
									const target = e.target as HTMLButtonElement;
									target.style.backgroundColor = '#5376F0';
									target.style.transform = 'translateY(0)';
								}}
							>
								
								ADD NEW
							</button>
						</div>
					)}
					<div className="mb-4" style={{ marginBottom: "100%" }}>
						<AdminTables 
							data={activeDomains}
							columns={tableColumns}
							rowSelectionEnabled={true}
							onRowSelect={(domain) => {
								if (domain.is_enabled) {
									handleDomainSelect(domain);
								}
							}}
							showSearch={false}
							selectedRowId={selectedDomain?.id}
							isLoading={false}
							selectedRowStyle={{
								backgroundColor: "#5376F0",
								color: "#FFFFFF"
							}}
							isRowDisabled={(domain) => !domain.is_enabled}
							containerStyle={{
								width: "100%",
								overflowX: "auto"
							}} 
						/>
					</div>
				</div>
			</div>
			<SideScreenLLM
				isOpen={isOpen}
				handleClose={closeChatHistory}
				LLMContent={LLMContent}
				loading={loading}
				handleNext={handleNext}
				handlePrev={handlePrev}
				showPagination={payloads.length > 1}
			/>
			<ScoreHistory
				isOpen={isHistoryOpen}
				handleClose={closeScoreHistory}
				loading={historyLoading}
				content={historyContent}
			/>
			<DomainEditSidebar
				isOpen={showDomainEditSidebar}
				onClose={handleCloseDomainEdit}
				domainData={editDomainData}
				onSuccess={handleDomainEditSuccess}
			/>
			<AddDomainSidebar
				isOpen={showAddDomainSidebar}
				onClose={handleCloseAddDomain}
				onSuccess={handleDomainAddSuccess}
				frameworkData={frameworkData}
			/>
		</div>
	);
};

export default Domains;
